<template>
  <Combobox
    v-slot="{ open }"
    as="div"
    class="relative"
    :multiple="multiple"
    :model-value="modelValue"
    @update:model-value="changeSelected">
    <ComboboxButton
      class="flex w-full items-center overflow-hidden rounded-full bg-gray-300 px-6 py-2 text-gray-200 transition hover:text-gray-100">
      <div
        class="my-2 mr-2 line-clamp-1 min-w-0 flex-shrink overflow-hidden border-none bg-gray-300 p-0 text-left ring-0 focus:ring-0 lg:mr-1 lg:h-6">
        {{ buttonLabel }}
      </div>

      <IconChevronDown
        class="ml-auto h-6 w-6 flex-shrink-0 fill-current transition"
        :class="open ? '-scale-y-100' : 'scale-y-100'" />
    </ComboboxButton>
    <TransitionRoot
      leave="transition ease-in duration-100"
      leave-from="opacity-100"
      leave-to="opacity-0">
      <ComboboxOptions class="absolute top-14 z-20 mt-2 w-full overflow-hidden rounded-xl bg-gray-300 text-gray-200">
        <div
          class="max-h-96 overflow-auto py-2"
          data-lenis-prevent>
          <ComboboxOption
            v-if="!multiple"
            class="cursor-default px-6 py-2 transition hover:bg-gray-250 hover:text-gray-100"
            :value="null">
            {{ placeholder }}
          </ComboboxOption>
          <ComboboxOption
            v-for="item in items"
            :key="item.key"
            :value="item"
            class="cursor-default px-6 py-2 transition hover:bg-gray-250 hover:text-gray-100"
            :class="getOptionClass(item)">
            {{ item.label }}
          </ComboboxOption>
        </div>
      </ComboboxOptions>
    </TransitionRoot>
  </Combobox>
</template>

<script setup lang="ts">
  import { Combobox, ComboboxButton, ComboboxOption, ComboboxOptions, TransitionRoot } from '@headlessui/vue'

  import type { ComboboxItem } from '../models/combobox'

  type DropdownProps = {
    items: ComboboxItem[]
    placeholder: string
    modelValue: ComboboxItem | ComboboxItem[]
    multiple?: boolean
  }

  const props = withDefaults(defineProps<DropdownProps>(), {
    multiple: false
  })

  const emit = defineEmits<{
    'update:model-value': [item: DropdownProps['modelValue']]
  }>()

  const buttonLabel = computed(() => {
    const isModelValueArray = Array.isArray(props.modelValue)

    if (!props.modelValue || (isModelValueArray && !props.modelValue.length)) {
      return props.placeholder
    }

    if (isModelValueArray) {
      return props.modelValue.map(item => item.label).join(', ')
    }

    return props.modelValue.label
  })

  function getOptionClass(item: ComboboxItem) {
    const isModelValueArray = Array.isArray(props.modelValue)

    if (
      (isModelValueArray && props.modelValue?.some(i => i.key === item.key)) ||
      (!isModelValueArray && props.modelValue?.key === item.key)
    ) {
      return 'bg-gray-250'
    }

    return ''
  }

  function changeSelected(item: DropdownProps['modelValue']) {
    emit('update:model-value', item)
  }
</script>
